/**
 * Light, dark, or whatever the device is set to.
 *
 * The stylesheet already carries both palettes: `:root` is light, a `prefers-color-scheme` block is
 * dark, and `:root[data-theme='…']` overrides either. So this only has to stamp one attribute.
 * `system` removes it, which hands the decision back to the media query — that is why following the
 * device keeps working with no listener and no JavaScript at all.
 *
 * The same three lines run from an inline script in index.html before the first paint, so a viewer who
 * asked for light on a dark device does not see a dark frame first. Keep the two in step.
 */
export type Appearance = 'system' | 'light' | 'dark';

const KEY = 'wasity.appearance';
export const APPEARANCES: Appearance[] = ['system', 'light', 'dark'];

export function readAppearance(): Appearance {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    // Private windows and blocked site data throw on read; following the device is the right default.
    return 'system';
  }
}

export function applyAppearance(a: Appearance): void {
  const root = document.documentElement;
  if (a === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', a);
  try {
    if (a === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, a);
  } catch {
    // Storage is a convenience here: the choice still applies to this tab.
  }
}

/** The order the sidebar button walks through, so one control covers all three. */
export function nextAppearance(a: Appearance): Appearance {
  return APPEARANCES[(APPEARANCES.indexOf(a) + 1) % APPEARANCES.length]!;
}
